import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "option"],
  _excluded2 = ["$isCustomComponent", "$toggleable"],
  _excluded3 = ["category"];
import * as ObjectTypeIds from 'customer-data-objects/constants/ObjectTypeIds';
import { SELF, makeDynamicFilterFamily, parseDynamicFilterFamilyId, testDynamicFilterFamily } from '../filterQueryFormat/DynamicFilterFamilies';
import { useCallback, useContext, useMemo } from 'react';
import { hasActivityFilters, hasActivityObjectDisabled } from '../permissions/gates';
import { useEditorActions } from './useEditorActions';
import { useStrategy } from './useStrategy';
import ActivityFilterInfoTooltip from '../components/common/ActivityFilterInfoTooltip';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import SegmentationFiltersInstanceContext from '../state/SegmentationFiltersInstanceContext';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { hasReorderedObjectSelect } from '../permissions/gates';
import { isCustomObjectType } from '../filterQueryFormat/ObjectTypeId';
import { jsx as _jsx } from "react/jsx-runtime";
export const STANDARD_OBJECT_IDS = [ObjectTypeIds.CONTACT_TYPE_ID, ObjectTypeIds.COMPANY_TYPE_ID, ObjectTypeIds.DEAL_TYPE_ID, ObjectTypeIds.TICKET_TYPE_ID];
export const ACTIVITY_OBJECTS = [ObjectTypeIds.CALL_TYPE_ID, ObjectTypeIds.COMMUNICATION_TYPE_ID, ObjectTypeIds.CONVERSATION_SESSION_TYPE_ID, ObjectTypeIds.EMAIL_TYPE_ID, ObjectTypeIds.MEETING_EVENT_TYPE_ID, ObjectTypeIds.NOTE_TYPE_ID, ObjectTypeIds.POSTAL_MAIL_TYPE_ID, ObjectTypeIds.PUBLISHING_TASK_TYPE_ID, ObjectTypeIds.TASK_TYPE_ID];
const ItemComponent = _ref => {
  let {
      children,
      option
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const domProps = _objectWithoutPropertiesLoose(rest, _excluded2);
  if ('options' in option || option.value !== ObjectTypeIds.ENGAGEMENT_TYPE_ID && option.value !== ObjectTypeIds.EMAIL_TYPE_ID) {
    return /*#__PURE__*/_jsx("span", Object.assign({}, domProps, {
      children: children
    }));
  }
  if (option.value === ObjectTypeIds.EMAIL_TYPE_ID) {
    return /*#__PURE__*/_jsx("span", Object.assign({}, domProps, {
      children: /*#__PURE__*/_jsx(Tooltip, {
        disabled: !option.disabled,
        placement: "right",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "newCustomerDataFilters.emailFilterInfo.description"
        }),
        children: children
      })
    }));
  }
  return /*#__PURE__*/_jsx("span", Object.assign({}, domProps, {
    children: /*#__PURE__*/_jsx(ActivityFilterInfoTooltip, {
      disabled: !option.disabled,
      children: children
    })
  }));
};
export const optionCategories = {
  STANDARD: 'STANDARD',
  ADDITIONAL: 'ADDITIONAL',
  CUSTOM: 'CUSTOM'
};
const getOptionCategory = (objectTypeId, baseFilterFamily, isCurrent = false) => {
  if (isCustomObjectType(objectTypeId)) {
    return optionCategories.CUSTOM;
  }
  if (STANDARD_OBJECT_IDS.includes(objectTypeId) && (isCurrent || objectTypeId !== baseFilterFamily)) {
    return optionCategories.STANDARD;
  }
  return optionCategories.ADDITIONAL;
};
const normalizeOption = option => {
  const rest = _objectWithoutPropertiesLoose(option, _excluded3);
  return rest;
};
const getObjectTypeSelectOptions = (baseFilterFamily, getEnabledAssociatedObjectTypeIds, getFilterFamilyObjectName, objectTypeSelectOptionsConfig = {}, useDataExplorer) => {
  const {
    getSelectedOptionAssociatedObjectText,
    getSelectedOptionCurrentObjectText,
    usePluralObjectNames
  } = objectTypeSelectOptionsConfig;
  const baseObjectName = getFilterFamilyObjectName(baseFilterFamily, usePluralObjectNames);
  const useReorderedObjectSelect = hasReorderedObjectSelect() || useDataExplorer;
  const currentObjectText = (getSelectedOptionCurrentObjectText === null || getSelectedOptionCurrentObjectText === void 0 ? void 0 : getSelectedOptionCurrentObjectText(baseObjectName)) || I18n.text('customerDataFiltersUI.objectTypeSelect.options.currentObject', {
    singularObjectType: baseObjectName
  });
  const currentObjectOption = {
    dropdownText: useReorderedObjectSelect ? currentObjectText : baseObjectName,
    text: currentObjectText,
    value: makeDynamicFilterFamily(SELF, baseFilterFamily),
    category: getOptionCategory(baseFilterFamily, baseFilterFamily, true)
  };
  const associatedObjectTypeIds = getEnabledAssociatedObjectTypeIds();
  const associatedObjectOptions = associatedObjectTypeIds.map(objectTypeId => {
    const objectName = getFilterFamilyObjectName(objectTypeId, usePluralObjectNames);
    const category = getOptionCategory(objectTypeId, baseFilterFamily);
    const sharedProps = {
      category,
      value: objectTypeId
    };
    if (hasActivityFilters() && hasActivityObjectDisabled() && baseFilterFamily !== ObjectTypeIds.ENGAGEMENT_TYPE_ID && objectTypeId === ObjectTypeIds.ENGAGEMENT_TYPE_ID) {
      sharedProps.disabled = true;
    }
    if (!hasActivityObjectDisabled() && baseFilterFamily !== ObjectTypeIds.EMAIL_TYPE_ID && objectTypeId === ObjectTypeIds.EMAIL_TYPE_ID) {
      sharedProps.disabled = true;
    }
    if (hasActivityFilters() && ACTIVITY_OBJECTS.includes(objectTypeId)) {
      sharedProps.badge = /*#__PURE__*/_jsx(Badge, {
        use: "new",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "newCustomerDataFilters.filterGroupEditor.newBadge"
        })
      });
    }
    const text = (getSelectedOptionAssociatedObjectText === null || getSelectedOptionAssociatedObjectText === void 0 ? void 0 : getSelectedOptionAssociatedObjectText(objectName)) || I18n.text('customerDataFiltersUI.objectTypeSelect.options.associatedObject', {
      singularObjectType: objectName
    });
    const dropdownText = objectTypeId === baseFilterFamily && useReorderedObjectSelect ? text : objectName;
    return Object.assign({}, sharedProps, {
      dropdownText,
      text,
      category
    });
  }).sort(({
    text: textA
  }, {
    text: textB
  }) => textA.localeCompare(textB));
  if (useReorderedObjectSelect) {
    const unsortedOptions = [currentObjectOption, ...associatedObjectOptions];
    const standardObjectOptions = unsortedOptions.filter(({
      category
    }) => category === optionCategories.STANDARD);
    const additionalObjectOptions = unsortedOptions.filter(({
      category
    }) => category === optionCategories.ADDITIONAL);
    const customObjectOptions = unsortedOptions.filter(({
      category
    }) => category === optionCategories.CUSTOM);
    const options = [];
    if (standardObjectOptions.length > 0) {
      options.push({
        text: I18n.text('customerDataFiltersUI.objectTypeSelect.standardObjects'),
        options: standardObjectOptions.map(normalizeOption)
      });
    }
    if (additionalObjectOptions.length > 0) {
      options.push({
        text: I18n.text('customerDataFiltersUI.objectTypeSelect.additionalObjects'),
        options: additionalObjectOptions.map(normalizeOption)
      });
    }
    if (customObjectOptions.length > 0) {
      options.push({
        text: I18n.text('customerDataFiltersUI.objectTypeSelect.customObjects'),
        options: customObjectOptions.map(normalizeOption)
      });
    }
    return options;
  }
  const options = [{
    text: I18n.text('customerDataFiltersUI.objectTypeSelect.currentObject'),
    options: [normalizeOption(currentObjectOption)]
  }];
  if (associatedObjectOptions.length > 0) {
    options.push({
      text: I18n.text('customerDataFiltersUI.objectTypeSelect.associatedObject'),
      options: associatedObjectOptions.map(normalizeOption)
    });
  }
  return options;
};
export const useObjectTypeSelect = objectTypeSelectOptionsConfig => {
  const {
    conditionPathToChangeFieldFor,
    conditionToRefineKeyPath,
    draftCondition,
    selectedObjectType,
    props: {
      baseFilterFamily,
      uiMessages: {
        filteringObjectDropdownLabel
      },
      useDataExplorer
    }
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    getEnabledAssociatedObjectTypeIds,
    getFilterFamilyObjectName
  } = useStrategy();
  const {
    onObjectTypeSelect
  } = useEditorActions();
  const options = useMemo(() => getObjectTypeSelectOptions(baseFilterFamily, getEnabledAssociatedObjectTypeIds, getFilterFamilyObjectName, objectTypeSelectOptionsConfig, useDataExplorer), [baseFilterFamily, getEnabledAssociatedObjectTypeIds, getFilterFamilyObjectName, objectTypeSelectOptionsConfig, useDataExplorer]);
  const value = useMemo(() => {
    const {
      filterFamily,
      isObjectOwnFilterBranch
    } = selectedObjectType || {
      filterFamily: baseFilterFamily,
      isObjectOwnFilterBranch: true
    };
    return isObjectOwnFilterBranch ? makeDynamicFilterFamily(SELF, filterFamily) : filterFamily;
  }, [baseFilterFamily, selectedObjectType]);
  const onChange = useCallback(evt => {
    const filterFamily = evt.target.value;
    onObjectTypeSelect({
      filterFamily: parseDynamicFilterFamilyId(SELF, filterFamily),
      isObjectOwnFilterBranch: testDynamicFilterFamily(SELF, filterFamily)
    });
  }, [onObjectTypeSelect]);
  const isFilterFamilyPredefined = Boolean(draftCondition && draftCondition.isAddingNew && draftCondition.filterFamily);
  const hasOnlyOneOption = options.length === 1 || isFilterFamilyPredefined || !!conditionPathToChangeFieldFor || !!conditionToRefineKeyPath;
  return {
    defaultValue: makeDynamicFilterFamily(SELF, baseFilterFamily),
    filteringObjectDropdownLabel,
    hasOnlyOneOption,
    isCurrentObject: !selectedObjectType || selectedObjectType.isObjectOwnFilterBranch,
    itemComponent: ItemComponent,
    options,
    value,
    onChange
  };
};